import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Plus, Trash2, Save, Loader2, Info, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sensible starting list — admin can add / remove any time.
const SEED = [
  'FAVN', 'IATA', 'ISO', 'GCM', 'LHR', 'JFK', 'MIA',
  'Cayman', 'microchip', 'endorsement', 'crate', 'cargo',
  'Cavachon', 'Cavapoo', 'Cockapoo', 'Dachshund', 'Rottweiler',
  'Weimaraner', 'Vizsla', 'Bulldog', 'Chihuahua', 'Pomeranian',
  'DEFRA', 'APHIS', 'USDA', 'Pawsome',
];

export const CustomDictionary = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings/dictionary`, { headers: auth });
      setWords(data.words || []);
    } catch (e) {
      logger.error('dictionary load', e);
      toast.error('Failed to load dictionary');
    } finally { setLoading(false); }
  };

  const save = async (list) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/settings/dictionary`,
        { words: list },
        { headers: auth },
      );
      setWords(data.words);
      // Broadcast so the global spell-check observer picks the new list up
      // without needing a page reload.
      window.dispatchEvent(new CustomEvent('pawsome:dictionary-updated', { detail: data.words }));
      toast.success('Dictionary saved');
    } catch (e) {
      logger.error('dictionary save', e);
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const addWord = async () => {
    const w = newWord.trim();
    if (!w) return;
    if (words.some((x) => x.toLowerCase() === w.toLowerCase())) {
      toast.info(`"${w}" is already in the dictionary`);
      setNewWord('');
      return;
    }
    const next = [...words, w].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    setNewWord('');
    await save(next);
  };

  const removeWord = async (w) => {
    if (!window.confirm(`Remove "${w}" from the dictionary?`)) return;
    await save(words.filter((x) => x !== w));
  };

  const addSeed = async () => {
    const merged = new Set(words.map((w) => w.toLowerCase()));
    const additions = SEED.filter((w) => !merged.has(w.toLowerCase()));
    if (additions.length === 0) {
      toast.info('All seed words are already in the dictionary');
      return;
    }
    await save([...words, ...additions].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return words;
    return words.filter((w) => w.toLowerCase().includes(q));
  }, [words, search]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dictionary-page">
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-primary" /></div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Custom Dictionary</h1>
              <p className="text-xs text-foreground/60">{words.length} word{words.length === 1 ? '' : 's'} · never flagged by spell-check</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-3xl space-y-5">
        {/* Add row */}
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold text-foreground/60 uppercase">Add a word</label>
              <Input
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWord(); } }}
                placeholder="e.g. FAVN, Cavapoo, Weimaraner"
                data-testid="new-word"
              />
            </div>
            <Button onClick={addWord} disabled={saving || !newWord.trim()} data-testid="add-word-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
            </Button>
            {words.length === 0 && (
              <Button variant="outline" onClick={addSeed} disabled={saving} data-testid="add-seed-btn">
                Add starter set
              </Button>
            )}
          </div>
          <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-md flex items-start gap-2">
            <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground/70">
              These words are automatically excluded from spell-check in <strong>rich-text</strong>
              fields (like the Email Composer body). In plain <strong>input</strong> or
              <strong> textarea</strong> fields, browsers own the underline — right-click a
              red-underlined word and choose <em>"Add to Dictionary"</em> to teach the browser too.
            </p>
          </div>
        </div>

        {/* Search + list */}
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
              <Input
                placeholder="Search dictionary…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search"
              />
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-foreground/60"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-foreground/50 text-sm">
              {words.length === 0 ? 'No words yet — add one above or tap "Add starter set".' : 'No matches for that search.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((w) => (
                <li key={w} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50" data-testid={`word-${w}`}>
                  <span className="text-sm">{w}</span>
                  <button
                    onClick={() => removeWord(w)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    data-testid={`remove-${w}`}
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomDictionary;
